<!--
* Provides a select field with options generated from an array of objects
*
* @param {number} [value=null] - The selected option index (null is the
*                                placeholder option)
*
* @param {string} [placeholder=''] - Placeholder text to be used as the
*                                    first option (null)
*
* @param {string} [displayKey='display'] - Which key should be used to determine
*                                         the value that should be shown
*                                         in the option, only applicable when
*                                         `this.options` is an array of objects
*
* @param {Array} [options=[]] - Either an array of objects or string
*                               used to populate the options
*
* @param {boolean} [isLoading=false] - If true a loading spinner is placed in
*                                      the option dropdown
*
* @param {string} [AdditionalInfo=''] - Additional info value
*
* @fires AppSelect#input
* @fires AppSelect#update:additionalInfo
-->


<!--
 * Options has been selected event.
 *
 * @event AppSelect#input
 * @property {Number} The value of the selected option
-->

<!--
 * Update additional info prop via .sync
 *
 * @event AppSelect#update:additionalInfo
 * @property {String} The new value of additional info
-->

<template>
  <div>
    <div class="select is-fullwidth" :class="{'is-loading': isLoading}">
      <select
        :selected="selected"
        @change="onChange"
        placeholder="placeholder"
        :class="{'placeholder': selected === null}"
        :disabled="isDisabled"
      >
      <option v-for="(option, index) in localOptions" :selected="isSelected(index)" :value="getOptionValue(index)">
          {{ getOptionDisplayText(option) }}
      </option>
      </select>
    </div>

    <input
      class="input"
      :value="additionalInfo"
      :placeholder="inputPlaceholder"
      @input="updateAdditionalInfo"
      v-if="options[selected] && options[selected].showInputField && !additionalInfoIsNumber"
      :disabled="isDisabled"
      autocomplete="off">

    <input
      class="input"
      :value="additionalInfo"
      :placeholder="inputPlaceholder"
      @input="updateAdditionalInfo"
      v-if="options[selected] && options[selected].showInputField && additionalInfoIsNumber"
      type="number"
      :disabled="isDisabled"
      autocomplete="off">
  </div>
</template>

<script>

export default {
  name: 'AppSelect',

  model: {
    prop: 'selected',
    event: 'change'
  },

  props: {
    selected: {
      type: Number,
      default: null
    },

    additionalInfo: {
      type: String,
      default: ''
    },

    placeholder: {
      type: String,
      default: null
    },

    inputPlaceholder: {
      type: String,
      default: ''
    },

    displayKey: {
      type: String,
      default: 'display'
    },

    options: {
      type: Array,
      default: []
    },

    isLoading: {
      type: Boolean,
      required: false
    },

    isDisabled: {
      type: Boolean,
      required: false
    },

    additionalInfoIsNumber: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  data () {
    return {
      localOptions: this.addPlaceholderOption(this.options)
    }
  },

  watch: {
    options (newVal) {
      this.localOptions = this.placeholder ? this.addPlaceholderOption(newVal) : newVal
    }
  },

  methods: {
    /**
     * Checks whether the current option is the selected option
     *
     * @param {number} index - Index of current option
     * @returns {boolean}  Whether the option is selected
     */
    isSelected (index) {
      // We check whether there is a placeholder so that we can get the valid
      // value for the item's index without the placeholder if one exists
      if (this.placeholder && index > 0) {
        return this.selected === index - 1
      }
      return this.selected === index
    },

    /**
     * Returns the value of an option
     *
     * @param {number} index - The index
     * @returns {number} The value of the selected option
     *                    or null for the placeholder
     */
    getOptionValue (index) {
      if (this.placeholder && index > 0) {
        return index - 1
      } else if (this.placeholder === null) {
        return index
      } else {
        return null
      }
    },

    /**
     * Get the display text for the option.
     *
     * If the option is an object `this.displayKey` is used to get the value,
     * else the option is returned
     *
     * @param {Object|string} option - The option
     * @returns {String} The display text
     */
    getOptionDisplayText (option) {
      if (typeof option === 'object') {
        return option[this.displayKey]
      }

      return option
    },

    /**
     * Adds a placeholder item as the first item in the options
     *
     * @param {Object[]} options - The options to add the placeholder to
     */
    addPlaceholderOption (options) {
      let localOptions = []
      if (options) {
        localOptions = options.slice()
      }

      let placeholderOption = {}
      placeholderOption[this.displayKey] = this.placeholder
      localOptions.unshift(placeholderOption)

      return localOptions
    },

    /**
     * Called when the value of selected changes, firing the input event
     *
     * @param {Object} event - Event object
     */
    onChange (event) {
      let selected = null
      if (event.target.value) {
        selected = this.options[event.target.value]
        Object.assign(selected, {
          index: parseInt(event.target.value)
        })
      }

      // If additional info has a value but field is not visible
      // then clear additional info
      if (
        selected !== null &&
        !selected.hasOwnProperty('showInputField') &&
        this.additionalInfo
      ) {
        this.$emit('update:additionalInfo', '')
      }

      // If invalid option was selected
      if (!selected) {
        selected = { id: null, index: null }
        this.$emit('update:additionalInfo', '')
      }

      this.$emit('change', selected)
    },

    /**
     * Update additional info prop
     *
     * @param {Object} event - The event object
     */
    updateAdditionalInfo (event) {
      this.$emit('update:additionalInfo', event.target.value)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  @import "../assets/sass/shared.scss";
  
  .select {
    height: auto;

    // Remove scrollbars from select fields for IE & Edge
    -ms-overflow-style: none;
  }

  .input {
    margin-top: $dims-spacing-small;
  }
</style>
